import { inject, injectable } from 'inversify'
import { observable, runInAction } from 'mobx'
import { Types } from '@core/ioc/types'
import type { ClockGateway } from '@core/services/clock.gateway'
import type { IdGeneratorGateway } from '@core/services/id-generator.gateway'
import type { LoggingGateway } from '@core/services/logging.gateway'
import type {
  OperationCancellationReason,
  OperationDefinition,
  OperationGroup,
  OperationOwner,
  OperationOwnerDefinition,
} from './operation.types'
import { OperationOwnerImpl, type OperationOwnerHost } from './operation.owner'
import type { OperationHandleImpl, OperationTimelineEntryInput } from './operation.handle'
import type { OperationDebugSnapshot, OperationDebugTimelineEntry } from './operation.debug.types'

/** Ring-buffer size for the debug timeline — bounded so a long-running dev session can't leak memory. */
const TIMELINE_CAPACITY = 200

/**
 * Singleton coordinator (spec §1, §9, §9.1). Owner-bound `OperationHandle`s do
 * the actual work; this class creates owners, holds the id->definition
 * registry (duplicate-id detection), and the global activity ledger that
 * `isBusy`/`pendingCount`/`isGroupBusy`/`pendingCountForGroup` are derived
 * from — nothing here is a manually toggled boolean.
 *
 * Bound by class in container.base.ts, never through `Types` (§9.1: `Types`
 * is reserved for interfaces/multi-bind identifiers in this codebase).
 */
@injectable()
export class OperationRepository implements OperationOwnerHost {
  @inject(Types.IIdGeneratorGateway)
  private readonly idGeneratorGateway!: IdGeneratorGateway

  @inject(Types.ILoggingGateway)
  private readonly loggingGateway!: LoggingGateway

  // Phase 2 (spec §12): every timeout deadline and retry backoff timer goes
  // through this, never a bare setTimeout, so tests advance a fake clock
  // instead of sleeping.
  @inject(Types.IClockGateway)
  private readonly clockGateway!: ClockGateway

  private readonly definitionsById = new Map<string, OperationDefinition<never, never>>()
  /** Definition id -> the raw owner id that first registered it (debug UI only — see registerDefinition). */
  private readonly registeredByOwnerId = new Map<string, string>()
  private readonly allHandles = new Set<OperationHandleImpl<never, never>>()
  private readonly globalActivity = observable.map<string, readonly OperationGroup[]>(undefined, { deep: false })
  private readonly timeline = observable.array<OperationDebugTimelineEntry>([], { deep: false })

  get isBusy(): boolean {
    return this.globalActivity.size > 0
  }

  get pendingCount(): number {
    return this.globalActivity.size
  }

  isGroupBusy(group: OperationGroup): boolean {
    for (const groups of this.globalActivity.values()) {
      if (groups.includes(group)) return true
    }
    return false
  }

  pendingCountForGroup(group: OperationGroup): number {
    let total = 0
    for (const groups of this.globalActivity.values()) {
      if (groups.includes(group)) total += 1
    }
    return total
  }

  /** Read-only introspection for a debug UI — see operation.debug.types.ts. */
  debugSnapshot(): OperationDebugSnapshot {
    const definitions = [...this.definitionsById.values()].map((definition) => ({
      id: definition.id,
      registeredByOwnerId: this.registeredByOwnerId.get(definition.id) ?? '',
      pending: definition.pending,
      safetyKind: definition.safety.kind,
      timeout: definition.timeout,
      retry: definition.retry,
    }))

    const handles = [...this.allHandles].map((handle) => handle.debugSnapshot())

    return { isBusy: this.isBusy, pendingCount: this.pendingCount, definitions, handles, timeline: [...this.timeline] }
  }

  createOwner(owner: OperationOwnerDefinition): OperationOwner {
    return new OperationOwnerImpl(owner, this)
  }

  cancelGroup(group: OperationGroup, reason: OperationCancellationReason): void {
    this.allHandles.forEach((handle) => handle.cancelGroupInternal(group, reason))
  }

  resetGroup(group: OperationGroup, reason: OperationCancellationReason): void {
    this.allHandles.forEach((handle) => handle.resetGroupInternal(group, reason))
  }

  cancelAll(reason: OperationCancellationReason): void {
    this.allHandles.forEach((handle) => handle.cancelAllInternal(reason))
  }

  // OperationOwnerHost — internal plumbing consumed by OperationOwnerImpl/OperationHandleImpl only.

  generateUnitId = (): string => {
    return this.idGeneratorGateway.generate()
  }

  after = (delayMs: number, callback: () => void): (() => void) => {
    return this.clockGateway.after(delayMs, callback)
  }

  log = (message: string, context: Record<string, unknown>): void => {
    this.loggingGateway.error(message, context)
  }

  registerDefinition(definition: OperationDefinition<never, never>, registeredByOwnerId: string): void {
    const existing = this.definitionsById.get(definition.id)
    if (existing !== undefined && existing !== definition) {
      throw new Error(
        `OperationRepository: "${definition.id}" is already registered with a different definition object. ` +
          'Two different definitions must not share one Operation id.',
      )
    }
    this.definitionsById.set(definition.id, definition)
    // Only the first registration sets this — every later re-registration
    // (repeated component mounts) is the exact same frozen definition object
    // from the exact same owner class by construction, so there is nothing
    // to update.
    if (!this.registeredByOwnerId.has(definition.id)) {
      this.registeredByOwnerId.set(definition.id, registeredByOwnerId)
    }
  }

  trackHandle(handle: OperationHandleImpl<never, never>): void {
    this.allHandles.add(handle)
  }

  untrackHandle(handle: OperationHandleImpl<never, never>): void {
    this.allHandles.delete(handle)
  }

  onGlobalUnitStart = (unitId: string, groups: readonly OperationGroup[]): void => {
    runInAction(() => this.globalActivity.set(unitId, groups))
  }

  onGlobalUnitEnd = (unitId: string): void => {
    runInAction(() => this.globalActivity.delete(unitId))
  }

  onTimelineEntry = (entry: OperationTimelineEntryInput): void => {
    runInAction(() => {
      this.timeline.unshift({ ...entry, timestampMs: this.clockGateway.now().getTime() })
      if (this.timeline.length > TIMELINE_CAPACITY) this.timeline.splice(TIMELINE_CAPACITY)
    })
  }
}
